import gsap from 'gsap'
import { SplitText } from 'gsap/SplitText'

gsap.registerPlugin(SplitText)

/**
 * Line-masked text transitions for the record caption. Shared by the slider
 * (swiping between records) and the page transitions (leaving / arriving), so
 * the home caption and a record page's caption hand over with the same motion.
 */

/**
 * Split the caption's heading + intro into masked lines. The split stays in place once the
 * lines have landed (reverting reflows the text a touch); splitting again replaces it.
 */
export function splitCaption(caption) {
	caption.__split?.revert()
	caption.__split = SplitText.create(caption.querySelectorAll('h1, p'), {
		type: 'lines',
		mask: 'lines',
		linesClass: 'record-caption__line',
	})
	return caption.__split
}

/** The caption's non-text parts (supertitle mark, index numbers): they fade with the lines. */
export function captionExtras(caption) {
	return [...caption.children].filter(child => !child.matches('h1, p'))
}

export function extrasOut(caption, vars = {}) {
	return gsap.to(captionExtras(caption), { autoAlpha: 0, duration: 0.3, ...vars })
}

export function extrasIn(caption, vars = {}) {
	return gsap.fromTo(captionExtras(caption), { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5, ...vars })
}

export function hideExtras(caption) {
	gsap.set(captionExtras(caption), { autoAlpha: 0 })
}

/** Lines slide up out of their masks. */
export function linesOut(lines, vars = {}) {
	return gsap.to(lines, { yPercent: -100, duration: 0.5, ease: 'power3.in', stagger: 0.04, ...vars })
}

/** Lines rise up into their masks (fromTo: they may already be parked at 100 by arriveCaption). */
export function linesIn(lines, vars = {}) {
	return gsap.fromTo(
		lines,
		{ yPercent: 100 },
		{ yPercent: 0, duration: 0.7, ease: 'power3.out', stagger: 0.1, ...vars }
	)
}

/** Resolve when a tween made by `create(vars)` completes - or is killed, so nothing waits forever. */
export function settled(create) {
	return new Promise(resolve => create({ onComplete: resolve, onInterrupt: resolve }))
}

/** Page leaving: animate a static caption's lines out (they stay hidden; the page is going). */
export function leaveCaption(caption) {
	if (!caption) return Promise.resolve()
	const split = splitCaption(caption)
	extrasOut(caption)
	return settled(vars => linesOut(split.lines, vars))
}

/** Page arriving: hide a static caption's lines now, animate them in once `ready` resolves. */
export async function arriveCaption(caption, ready = Promise.resolve()) {
	if (!caption) return
	const split = splitCaption(caption)
	gsap.set(split.lines, { yPercent: 100 })
	hideExtras(caption)
	await ready
	if (!caption.isConnected) return // page already gone
	extrasIn(caption)
	await settled(vars => linesIn(split.lines, vars))
}
